import electricLogo from '@/assets/electric_logo_red.svg';
import Nav from './SideNavBase';
import { generateNavItems } from './generateNavItems';
import NavLogo from '../Logo';
import { SideNavItem } from './SideNavItem';
import { useBreakpoint } from '@electricjs/arc';
import isVisible from '@/helpers/isVisible';
import { NavLink as RouterNavLink, NavLinkProps } from 'react-router-dom';
import { useAppSelector } from '@/redux/store';
import { OrganizationUserRoleSlugs } from '@/types/users';
import { useOrganizationHasEntitlements } from '@/hooks/useOrganizationHasEntitlement';
import { PermissionEnum } from '@electricjs/core_entity-client';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetHelpfulLinksQuery } from '@/redux/slices/helpfulLinksApiSlice';
import { useGetOrganizationApplicationsQuery } from '@/redux/slices/organizationApiSlice';
import { useMemo } from 'react';
import { useOrganizationHasISVAddOns } from '@/hooks/useOrganizationHasISVAddOns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { useHasProPlan } from '@/hooks/useHasProPlan';
import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';
import useIsProdSupport from '@/hooks/useIsProdSupport';

/**
 * Creates a wrapper when an onClick method is provided.
 * This is primarily used to close the mobile menu when a menu item is clicked.
 */
function NavLinkWithOnClick(onClick?: () => void) {
  return function NavLink(props: NavLinkProps) {
    return <RouterNavLink {...(onClick ? { onClick } : {})} {...props} />;
  };
}

/**
 * Wrapper for the sidenav that renders navigation items
 * Each SideNavItem will automatically receive the following props from SideNavBase:
 * - toggleSideNav: Function to toggle the sidenav collapse state
 * - isCollapsed: Boolean indicating if the sidenav is collapsed
 */
const SideNavWrapper = ({
  mobileOnMenuClick,
}: {
  mobileOnMenuClick?: () => void;
}) => {
  const { md } = useBreakpoint();
  const loggedUser = useAppSelector(state => state.loggedUser);
  const { t } = useTranslation();
  const isAdminOrSupportRole = loggedUser?.organizationUserRoles?.some(
    role =>
      role.slug === OrganizationUserRoleSlugs.SUPER_ADMIN ||
      role.slug === OrganizationUserRoleSlugs.PRODUCT_SUPPORT
  );

  const isProductSupport = useIsProdSupport();

  const hasProPlan = useHasProPlan();
  const {
    hasApplicationsEntitlement,
    hasEmployeeGroupsEntitlement,
    hasThreatdownSecurityDashboardEntitlement: hasAntivirusEntitlement,
    hasHarmonyEmailAndCollaborationSecurityDashboardEntitlement:
      hasEmailSecurityEntitlement,
    hasGigawattEntitlement,
    hasItPlanEntitlement: hasITPlanEntitlement,
    hasDesktopAppEntitlement: hasElectricAppEntitlement,
    hasEmployeeDataBreachesEntitlement: hasEmployeeDataBreachEntitlement,
    hasSupportCenterEntitlement,
    hasHelpfulLinksEntitlement,
    hasMarketplaceEntitlement,
    hasJumpcloudMdmEntitlement,
    hasXboardingsEntitlement,
    hasWebhooksEntitlement,
  } = useOrganizationHasEntitlements();

  const canReadAssets = useUserHasOrganizationPermission(
    PermissionEnum.Assetsread
  );

  const canReadShipments = useUserHasOrganizationPermission(
    PermissionEnum.Shipmentsread
  );

  const canReadOrganizationPackage = useUserHasOrganizationPermission(
    PermissionEnum.OrganizationPackageread
  );

  const canReadOrganizationAddOns = useUserHasOrganizationPermission(
    PermissionEnum.OrganizationAddOnsread
  );

  const canReadWebhooks = useUserHasOrganizationPermission(
    PermissionEnum.Webhooksread
  );

  const {
    itHubShipmentQrCodeHistory,
    ithFeatureDescriptionPages,
    ithOutboundWebhooks,
    ithDivisions,
  } = useFlags();

  const { hasAnyGigawattAccess, isGigawattBasicOnly } = useAIChatFeatureFlags();

  const organizationId = useGetOrganizationId();
  const { data: helpfulLinks } = useGetHelpfulLinksQuery(organizationId);
  const { data: appsData } = useGetOrganizationApplicationsQuery({
    organizationId,
  });

  const hasHelpfulLinks = useMemo(() => {
    const helpfulLinksExist = helpfulLinks && helpfulLinks.length > 0;
    const appsExist =
      appsData?.applications && appsData?.applications.length > 0;
    return helpfulLinksExist || appsExist;
  }, [helpfulLinks, appsData]);

  const hasISVAddOns = useOrganizationHasISVAddOns();
  // If the mobile menu is open, we want to close it when a menu item is clicked
  const NavLink = mobileOnMenuClick
    ? NavLinkWithOnClick(mobileOnMenuClick)
    : RouterNavLink;

  const logoProps = {
    logoElement: (
      <img
        alt="Electric"
        className="nav-logo__img"
        data-testid="nav-logo"
        height="30"
        src={electricLogo}
        width="102"
      />
    ),
    logoLinkProps: {
      href: '/',
    },
  };

  const navItems = generateNavItems({
    hasProPlan,
    isAdminOrSupportRole,
    isProductSupport,
    NavLink,
    entitlements: {
      hasApplicationsEntitlement,
      hasEmployeeGroupsEntitlement,
      hasAntivirusEntitlement,
      hasEmailSecurityEntitlement,
      hasGigawattEntitlement,
      hasITPlanEntitlement,
      hasElectricAppEntitlement,
      hasEmployeeDataBreachEntitlement,
      hasSupportCenterEntitlement,
      hasHelpfulLinksEntitlement,
      hasMarketplaceEntitlement,
      hasJumpcloudMdmEntitlement,
      hasXboardingsEntitlement,
      hasWebhooksEntitlement,
    },
    permissions: {
      canReadAssets,
      canReadShipments,
      canReadOrganizationPackage,
      canReadOrganizationAddOns,
      canReadWebhooks,
    },
    hasHelpfulLinks,
    hasISVAddOns,
    showGigawattItem: hasAnyGigawattAccess,
    isGigawattBasicOnly,
    flags: {
      itHubShipmentQrCodeHistory,
      ithFeatureDescriptionPages,
      ithOutboundWebhooks,
      ithDivisions,
    },
    t,
  });

  return (
    <Nav mobilePositionTop="auto">
      {md ? <NavLogo {...logoProps} /> : null}
      {navItems
        ? navItems
            .filter(isVisible)
            .map(item => (
              <SideNavItem key={item.text ?? item.sectionHeading} {...item} />
            ))
        : null}
    </Nav>
  );
};

export default SideNavWrapper;
